//
//  DefaultLayout.tsx
//
//  Copyright:
//    NewtonGreen Technologies Pty. Ltd.
//    Level 4, 175 Scott Street
//    Newcastle, NSW, 2300
//    Australia
//
//    E-mail: support@newtongreen.com
//    Tel: (02) 4925 5288
//
//    All Rights Reserved.
//
//  Function of this module:
//    This module renders the Default Layout component.
//

import { AppShell, Box, Drawer, Group, Paper, useComputedColorScheme } from "@mantine/core";
import { useDisclosure, useLocalStorage, useMediaQuery } from "@mantine/hooks";
import { useContext, useMemo, useState, type FC, type PropsWithChildren } from "react";

import HelpPanel from "src/components/help/HelpPanel";
import HelpTourTarget from "src/components/help/HelpTourTarget";
import NoticeBanners from "src/components/notices/NoticeBanners";
import PinnedQaProgramChecklist, {
    collapsedPinnedChecklistWidth,
    expandedPinnedChecklistWidth
} from "src/components/qaProgramChecklist/PinnedQaProgramChecklist";
import HelpProvider from "src/components/providers/help/HelpProvider";
import LayoutContext from "src/contexts/layouts/LayoutContext";
import PageAsideContext from "src/contexts/layouts/PageAsideContext";
import PageFrameContext from "src/contexts/layouts/PageFrameContext";
import { helpTourTargets } from "src/help/helpTours";
import useCurrentUser from "src/hooks/authentication/useCurrentUser";
import useCurrentUserPermissions from "src/hooks/authentication/useCurrentUserPermissions";
import useHelp from "src/hooks/help/useHelp";
import usePinnedQaProgramChecklist from "src/hooks/preferences/usePinnedQaProgramChecklist";
import { filterLayoutLinksByPermission } from "src/utilities/hasLayoutLinkPermission";

import LayoutContent from "../shared/LayoutContent";
import LayoutFooter from "../shared/LayoutFooter";
import LazySearch from "../shared/LazySearch";
import Header, { modernHeaderHeight } from "./Header";
import ModernBreadcrumbs from "./ModernBreadcrumbs";
import SubMenus, {
    collapsedSubMenuWidth,
    expandedSubMenuWidth,
    getSubMenusWidth
} from "./SubMenus";

/**
 * Builds the sidebar preference key from a trimmed, lowercase username.
 *
 * Postconditions: Falsy usernames use anonymous; whitespace-only names leave an empty suffix.
 */
/** Width the shell reserves for the desktop help panel. */
export const helpPanelWidth = 360;

export const getSidebarStorageKey = (username?: string) =>
    `tmd-modern-sidebar-collapsed:${(username || "anonymous").trim().toLowerCase()}`;

/**
 * Composes the modern responsive shell with per-user persisted sidebar collapse state.
 *
 * Postconditions: Empty permission-filtered submenu levels are omitted before width calculation.
 * Mobile disclosure remains independent of desktop collapse. Storage updates and disclosure
 * actions are observed on later renders; shared content retains responsibility for page gating.
 */
const ModernShell: FC<PropsWithChildren> = ({ children }) => {
    const layoutContext = useContext(LayoutContext);
    const permissions = useCurrentUserPermissions();
    const user = useCurrentUser();
    const colorScheme = useComputedColorScheme();
    const [mobileOpened, mobileHandlers] = useDisclosure(false);
    const [pinnedMobileOpened, pinnedMobileHandlers] = useDisclosure(false);
    const { pin } = usePinnedQaProgramChecklist();
    const help = useHelp();
    const helpAsideOpened = help.opened && help.desktop;
    // Pages may portal supporting content, such as a table of contents, beside the content card.
    const [pageAside, setPageAside] = useState<HTMLElement | null>(null);
    const pageAsideAvailable = useMediaQuery("(min-width: 62em)") ?? false;
    // Pages are framed by the content card unless they lay out their own cards.
    const [framed, setFramed] = useState(true);
    const pageFrame = useMemo(() => ({ setFramed }), []);
    const [sidebarCollapsed, setSidebarCollapsed] = useLocalStorage<boolean>({
        key: getSidebarStorageKey(user?.username),
        defaultValue: false,
        getInitialValueInEffect: false
    });

    const permittedSubMenus = useMemo(
        () =>
            (layoutContext.subMenus ?? [])
                .map(subMenu => ({
                    ...subMenu,
                    items: filterLayoutLinksByPermission(
                        permissions,
                        subMenu.items,
                        subMenu.context
                    )
                }))
                .filter(subMenu => subMenu.items.length > 0),
        [layoutContext.subMenus, permissions]
    );
    const hasSubMenus = permittedSubMenus.length > 0;
    const pinnedChecklistWidth = pin
        ? pin.collapsed
            ? collapsedPinnedChecklistWidth
            : expandedPinnedChecklistWidth
        : 0;
    const navbarWidth = getSubMenusWidth(permittedSubMenus, sidebarCollapsed);

    return (
        <AppShell
            header={{ height: modernHeaderHeight }}
            navbar={{
                width: { base: expandedSubMenuWidth, sm: navbarWidth },
                breakpoint: "sm",
                collapsed: {
                    desktop: !hasSubMenus,
                    mobile: !mobileOpened
                }
            }}
            aside={
                pin || helpAsideOpened
                    ? {
                          width: pinnedChecklistWidth + (helpAsideOpened ? helpPanelWidth : 0),
                          breakpoint: "sm",
                          collapsed: { mobile: true }
                      }
                    : undefined
            }
            padding={0}
            withBorder
            styles={{
                root: {
                    minHeight: "100vh",
                    backgroundColor:
                        colorScheme === "dark"
                            ? "var(--mantine-color-dark-8)"
                            : "var(--mantine-color-gray-1)"
                },
                main: { minHeight: "100vh" }
            }}
        >
            <AppShell.Header>
                <Header
                    menuItems={layoutContext.menuItems}
                    mobileOpened={mobileOpened}
                    toggleMobile={mobileHandlers.toggle}
                    hasDesktopSidebar={hasSubMenus}
                    desktopSidebarWidth={collapsedSubMenuWidth}
                    sidebarCollapsed={sidebarCollapsed}
                    toggleSidebar={() => setSidebarCollapsed(current => !current)}
                    hasPinnedChecklist={Boolean(pin)}
                    openPinnedChecklist={pinnedMobileHandlers.open}
                />
            </AppShell.Header>
            <AppShell.Navbar>
                <SubMenus
                    subMenus={permittedSubMenus}
                    collapsed={sidebarCollapsed}
                    menuItems={layoutContext.menuItems}
                    mobileOpened={mobileOpened}
                    onNavigate={mobileHandlers.close}
                />
            </AppShell.Navbar>
            {(pin || helpAsideOpened) && (
                <AppShell.Aside visibleFrom="sm">
                    {/* The help panel sits on the outer edge, beside any pinned checklist. */}
                    <Group
                        h="100%"
                        gap={0}
                        wrap="nowrap"
                        align="stretch"
                    >
                        {pin && (
                            <Box
                                w={pinnedChecklistWidth}
                                style={{ flexShrink: 0 }}
                            >
                                <PinnedQaProgramChecklist collapsed={pin.collapsed} />
                            </Box>
                        )}
                        {helpAsideOpened && (
                            <Box
                                w={helpPanelWidth}
                                style={{
                                    flexShrink: 0,
                                    borderLeft: pin
                                        ? "1px solid var(--app-shell-border-color)"
                                        : undefined
                                }}
                            >
                                <HelpPanel onClose={help.close} />
                            </Box>
                        )}
                    </Group>
                </AppShell.Aside>
            )}
            <AppShell.Main>
                <Group
                    gap={0}
                    align="stretch"
                    wrap="nowrap"
                    mih="calc(100dvh - var(--app-shell-header-height, 0px))"
                >
                    {/* Spans the main area's full height, flush with the header and navbar, so aside
                        content can fill it and follow the page. Empty, it takes no width. */}
                    {pageAsideAvailable && <Box ref={setPageAside} />}
                    <Box style={{ flex: 1, minWidth: 0 }}>
                        <NoticeBanners />
                        <ModernBreadcrumbs />
                        <Box p="md">
                            <PageFrameContext.Provider value={pageFrame}>
                                <PageAsideContext.Provider
                                    value={pageAsideAvailable ? pageAside : null}
                                >
                                    {/* One element either way, so changing the frame never remounts the page. */}
                                    <Paper
                                        withBorder={framed}
                                        radius="md"
                                        p={framed ? "sm" : 0}
                                        shadow={framed ? "xs" : undefined}
                                        bg={framed ? undefined : "transparent"}
                                    >
                                        <LayoutContent>{children}</LayoutContent>
                                        <LayoutFooter />
                                    </Paper>
                                </PageAsideContext.Provider>
                            </PageFrameContext.Provider>
                        </Box>
                    </Box>
                </Group>
                <LazySearch />
            </AppShell.Main>
            <Drawer
                opened={pinnedMobileOpened}
                onClose={pinnedMobileHandlers.close}
                title="Pinned QA Program Checklist"
                position="right"
                size="100%"
                hiddenFrom="sm"
            >
                <PinnedQaProgramChecklist onClose={pinnedMobileHandlers.close} />
            </Drawer>
            <Drawer
                opened={help.opened && !help.desktop}
                onClose={help.close}
                withCloseButton={false}
                position="right"
                size="100%"
                hiddenFrom="sm"
                padding={0}
            >
                <Box h="100dvh">
                    <HelpPanel onClose={help.close} />
                </Box>
            </Drawer>
        </AppShell>
    );
};

/**
 * Composes the modern shell inside the help provider so the header, aside and page tour targets
 * share one help state.
 *
 * Postconditions: The help panel narrows the main content on sm and wider (it is an aside, not an
 * overlay) and is a full-width drawer below sm.
 */
const DefaultLayout: FC<PropsWithChildren> = ({ children }) => (
    <HelpProvider>
        <ModernShell>{children}</ModernShell>
    </HelpProvider>
);

export default DefaultLayout;
